<template>
  <div id="chat_list" class="flex flex-col gap-1 p-2 proxima_nova">
    <div
      v-for="(chat, index) in chats"
      :key="`${chat?.id}_chat_${index}`"
      :data-chat-id="chat?.id"
      class="flex flex-row gap-2 items-center py-[10px] px-2 rounded-lg chat_item_container cursor-pointer"
      :class="{
        active: isChatActive(chat),
      }"
      @click="handleChatSelect(chat)"
      @mouseenter="hoveredChatId = chat?.id"
      @mouseleave="hoveredChatId = null"
    >
        <div
          v-if="isGroupDM(chat)"
          class="stacked_avatar shrink-0"
        >
          <ProfileIcon
            :size="26"
            :font="9"
            :data="getGroupDMMembers(chat)[0]"
            class="stacked_avatar_top"
          />
          <ProfileIcon
            :size="27"
            :font="9"
            :data="getGroupDMMembers(chat)[1]"
            :class="getGroupDMMembers(chat)[1]?.profile_pic ? 'stacked_avatar_bottom_no_border' : 'stacked_avatar_bottom'"
          />
        </div>
        <div
          v-else-if="isChannelGroup(chat) && chat.profile_pic"
          class="shrink-0 w-8 h-8 rounded-full overflow-hidden"
        >
          <img :src="chat.profile_pic" alt="group avatar" class="w-full h-full object-cover" />
        </div>
        <div
          v-else-if="isChannelGroup(chat)"
          class="flex items-center justify-center rounded-full shrink-0 w-8 h-8"
          :class="getAuthUserProfileColor({ name: chat.name }).class"
          :style="getAuthUserProfileColor({ name: chat.name }).style || {}"
        >
          <ThemeImage
            :icon="imageNames.GROUP"
            alt="group_icon"
            class="size-5"
          />
        </div>
        <ProfileIcon
          v-else
          :size="32"
          :font="12"
          :data="getChatProfileData(chat)"
          :showPresence="isChatPresenceEnabled && !!getDMOtherUserId(chat)"
          :userId="getDMOtherUserId(chat)"
        />

        <div class="flex flex-col gap-1.5 w-full truncate">
          <div class="flex flex-row items-center justify-between truncate">
            <div class="flex items-center gap-1 truncate">
              <span class="text-[15px] truncate font-semibold" :class="getChatNameClasses(chat)">
                {{ getChatDisplayName(chat) }}
              </span>
            </div>

            <span
              v-if="!chat?.is_placeholder"
              class="text-[12px] text-nowrap font-normal"
              :class="chat?.unread_count > 0 ? 'count_text_color' : 'secondary_color'"
            >
              {{ getChatTime(chat) }}
            </span>
          </div>

          <div class="flex flex-row items-center justify-between truncate">
            <div
              :class="`text-[14px] truncate ${hasDraft(chat?.id) ? 'italic link_color' : chat?.unread_count > 0 ? 'font-bold primary_color' : 'font-normal secondary_color'}`"
            >
              {{ getLastMessageDisplay(chat) || (isSelfDM(chat) ? 'Message yourself' : chat?.is_placeholder ? getPlaceholderGreeting(chat) : 'No messages yet') }}
            </div>

            <div class="flex flex-row items-center justify-end gap-1">
              <div
                v-if="chat?.unread_count > 0"
                class="flex items-center justify-center text-xs text-white font-semibold pt-[1px] px-[4px] leading-[14px] !min-w-[16px] count_tag"
              >
                {{ chat?.unread_count }}
              </div>
              <ThemeImage
                  v-if="isGroupPinned(chat?.id)"
                  :icon="imageNames.STAR_FILLED_GREY"
                  class="size-4 shrink-0 opacity-70"
                  alt="pinned"
              />
            </div>
          </div>
        </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { formatChatListTime } from "@/src/utils/dateTimeUtils.js";
import { chatTypes, chatRecipientTypes, PLACEHOLDER_GREETINGS } from "@/src/constants/chatConstants.js";
import { renderEntity, getProfileColor, getAuthUserProfileColor } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import eventBus from "@/src/utils/eventBus.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import { useDMResolution } from "@/src/composables/useDMResolution.js";
const store = useStore();
const { resolveOtherDMUserName, resolveOtherDMUser } = useDMResolution();

const props = defineProps({
  chats: { type: Array, required: true },
});

const emit = defineEmits(["chat-selected"]);

const isEmbeddedMode = inject("embeddedChatMode", false);
const openEmbeddedChat = inject("openEmbeddedChat", null);

const isChatPresenceEnabled = computed(() => !!localStorage.getItem("chat_presence"));
const isGroupPinned = (groupId) => store.getters["chats/isGroupPinned"](groupId);

const hoveredChatId = ref(null);
const draftsVersion = ref(0);
const timeTick = ref(0);
let timeTickInterval = null;

const activeChatID = computed(
  () => store.getters["chats/activeChat"]?.id || null,
);

const allUsers = computed(() => store.getters["users/users"] || []);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);

const getChatTime = (chat) => {
  timeTick.value;
  return formatChatListTime(chat?.last_message_time) || formatChatListTime(chat?.created_at);
};

const draftCache = ref(new Map());

const getDraft = (chatId) => {
  try {
    draftsVersion.value;
    
    if (draftCache.value.has(chatId)) {
      return draftCache.value.get(chatId);
    }
    
    const draftKey = `chat_draft_${chatId}`;
    const draftJson = localStorage.getItem(draftKey);
    
    if (!draftJson) {
      draftCache.value.set(chatId, null);
      return null;
    }
    
    const draft = JSON.parse(draftJson);
    draftCache.value.set(chatId, draft);
    return draft;
  } catch (e) {
    draftCache.value.set(chatId, null);
    return null;
  }
};

const hasDraft = (chatId) => {
  const draft = getDraft(chatId);
  return !!(draft?.text?.trim());
};

const getDraftPreview = (chatId) => {
  const draft = getDraft(chatId);
  
  if (draft?.text?.trim()) {
    const previewText = draft.text.trim().replace(/\n/g, ' ');
    return `Draft: ${previewText}`;
  }
  
  return null;
};

const getPlaceholderGreeting = (chat) => {
  const index = Math.abs(String(chat?.id || "").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0)) % PLACEHOLDER_GREETINGS.length;
  return PLACEHOLDER_GREETINGS[index];
};

const getLastMessageDisplay = (chat) => {
  try {
    if (!chat) return null;
    
    if (hasDraft(chat.id)) {
      return getDraftPreview(chat.id);
    }
    
    const lastMessage = chat?.last_message;
    const lastMessageAttachments = chat?.last_message_attachments;
    
    if (!lastMessage || !lastMessage.trim()) {
      if (lastMessageAttachments && lastMessageAttachments.length > 0) {
        const firstAttachment = lastMessageAttachments[0];
        const fileName = firstAttachment?.file_name || firstAttachment?.name;
        if (fileName) {
          return `📎 ${fileName}`;
        }
        return "📎 Attachment";
      }
      
      if (chat?.last_message_has_attachments || chat?.has_attachments) {
        return "📎 Attachment";
      }
      
      return null;
    }
    
    return lastMessage;
  } catch (error) {
    console.error("An error occurred while getting last message display: ", error);
    return null;
  }
};

const resolveUserByName = (name) => {
  if (!name) return null;
  const normalizedName = name.toLowerCase().trim();
  return allUsers.value.find(u => {
    return renderEntity(u).toLowerCase().trim() === normalizedName;
  }) || null;
};

const getChatDisplayName = computed(() => {
  return (chat) => {
    try {
      if (!chat) return "Unknown";

      if (isSelfDM(chat)) {
        if (chat.is_placeholder) {
          const otherUser = chat.other_user || chat.user_data;
          if (otherUser) return `${renderEntity(otherUser)} (You)`;
        }
        const currentUser = allUsers.value.find(u => Number(u.id) === Number(currentUserId.value));
        if (currentUser) return `${renderEntity(currentUser)} (You)`;
      }

      if (chat?.is_placeholder) {
        const otherUser = chat.other_user || chat.user_data;
        if (otherUser) return renderEntity(otherUser);
      }

      const otherName = resolveOtherDMUserName(chat);
      if (otherName) return otherName;

      return chat.name || "Unknown";
    } catch (error) {
      console.error("An error occurred while getting chat display name: ", error);
      return chat?.name || "Unknown";
    }
  };
});

const enrichWithStoreUser = (user) => {
  if (!user?.id) return user;
  const found = allUsers.value.find((u) => u.id === user.id);
  return found || user;
};

const getChatProfileData = computed(() => {
  return (chat) => {
    try {
      if (!chat) return chat;

      if (chat?.is_placeholder) {
        const otherUser = chat.other_user || chat.user_data;
        if (otherUser) return enrichWithStoreUser(otherUser);
      }

      const isOneOnOneDM = chat?.is_dm === true &&
                          (chat?.type === chatTypes.DM || chat?.type === chatTypes.SELF_DM) &&
                          chat?.recipient_type === chatRecipientTypes.ONE_ON_ONE;

      if (isOneOnOneDM && chat.name) {
        if (chat.type === chatTypes.SELF_DM) {
          const currentUser = allUsers.value.find(u => Number(u.id) === Number(currentUserId.value));
          if (currentUser) return currentUser;
        }
        const otherDMUser = resolveOtherDMUser(chat);
        if (otherDMUser) return otherDMUser;
        const storeUser = resolveUserByName(chat.name);
        if (storeUser) return storeUser;
      }

      return chat;
    } catch (error) {
      console.error("An error occurred while getting chat profile data: ", error);
      return chat;
    }
  };
});

const isSelfDM = (chat) => {
  if (!chat?.is_dm || chat?.recipient_type !== chatRecipientTypes.ONE_ON_ONE) return false;
  return chat?.type === chatTypes.SELF_DM;
};

const isDMChat = (chat) => {
  if (!chat) return false;
  return chat.is_dm === true &&
    (chat.type === chatTypes.DM || chat.type === chatTypes.SELF_DM) &&
    chat.recipient_type === chatRecipientTypes.ONE_ON_ONE;
};

const getDMOtherUserId = computed(() => {
  return (chat) => {
    if (!chat || !isDMChat(chat)) return null;
    if (isSelfDM(chat)) return null;
    if (chat.is_placeholder) {
      const otherUser = chat.other_user || chat.user_data;
      return otherUser?.id || null;
    }
    const otherDMUser = resolveOtherDMUser(chat);
    if (otherDMUser) return otherDMUser.id;
    const otherUser = chat.name ? resolveUserByName(chat.name) : null;
    return otherUser?.id || null;
  };
});

const isGroupDM = (chat) => {
  return chat?.type === chatTypes.GROUP_DM;
};

const isChannelGroup = (chat) => {
  return !chat?.is_dm && (chat?.type === "public" || chat?.type === "private" || chat?.type === "external");
};


const getGroupDMMembers = (chat) => {
  if (chat?.members?.length > 0) {
    return chat.members
      .map(m => {
        const storeUser = allUsers.value.find(u => Number(u.id) === Number(m.user_id));
        return storeUser || { id: m.user_id, name: m.user_name, avatar_url: m.profile_pic };
      })
      .slice(0, 2);
  }
  if (!chat?.name) return [];
  const nameSegments = chat.name.split(",").map(s => s.trim()).filter(Boolean);
  const resolved = [];
  for (const segment of nameSegments) {
    if (resolved.length >= 2) break;
    const user = resolveUserByName(segment);
    if (user && Number(user.id) !== Number(currentUserId.value)) {
      resolved.push(user);
    }
  }
  return resolved;
};

const getChatNameClasses = computed(() => {
  return (chat) => {
    if (activeChatID.value === chat?.id && chat?.unread_count > 0) {
      return "primary_color";
    }

    if (activeChatID.value === chat?.id) {
      return "primary_color";
    }

    if (chat?.unread_count > 0) {
      return "primary_color font-bold";
    }

    return "secondary_color";
  };
});

const isChatActive = computed(() => {
  return (chat) => {
    if (!chat?.id) return false;

    if (isEmbeddedMode) {
      const openChatIds = store.getters["embeddedChats/openChatIds"] || [];
      if (openChatIds.length === 0) return false;
      return store.getters["embeddedChats/isChatOpen"](chat.id);
    }

    if (!activeChatID.value) return false;
    const normalizedActiveId = String(activeChatID.value).replace(/^group_/, '');
    const normalizedChatId = String(chat.id).replace(/^group_/, '');
    return normalizedActiveId === normalizedChatId;
  };
});

const handleChatSelect = async (chat) => {
  try {
    if (isEmbeddedMode && openEmbeddedChat) {
      openEmbeddedChat(chat);
    } else {
      store.dispatch("chats/setActiveChat", chat);
    }
    emit("chat-selected", chat);

    if (chat?.unread_count > 0) {
      store.dispatch("chats/updateUnreadCount", {
        groupId: chat.id,
        unreadCount: 0,
      });
    }
  } catch (error) {
    console.error("An error occurred while selecting chat: ", error);
  }
};

watch(activeChatID, (newId) => {
  if (!newId) return;
  nextTick(() => {
    const chatListEl = document.getElementById("chat_list");
    if (!chatListEl) return;
    const activeEl = chatListEl.querySelector(`[data-chat-id="${newId}"]`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  });
});

const handleDraftsUpdate = () => {
  draftCache.value.clear();
  draftsVersion.value++;
};

onMounted(() => {
  eventBus.on("draft-updated", handleDraftsUpdate);
  timeTickInterval = setInterval(() => { timeTick.value++; }, 60000);
});

onUnmounted(() => {
  eventBus.off("draft-updated", handleDraftsUpdate);
  if (timeTickInterval) clearInterval(timeTickInterval);
});
</script>

<style scoped>
.chat_item_container {
  border: 1px solid transparent;
  transition:
    background-color 0.4s ease-in-out,
    border-color 0.4s ease-in-out,
    color 0.4s ease-in-out;
}

.chat_item_container.unread {
  background-color: var(--white-opacity-5);
}

.chat_item_container:hover,
.chat_item_container.active {
  background: var(--white-opacity-12);
}

.chat_item_container.active {
  border: 1px solid var(--white-opacity-24);
}

.stacked_avatar {
  position: relative;
  width: 32px;
  height: 32px;
}

.stacked_avatar_top {
  position: absolute;
  top: -5px;
  left: -5px;
  z-index: 2;
}

.stacked_avatar_bottom {
  border: 3px solid var(--background-color);
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 3;
}

.stacked_avatar_bottom_no_border {
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 3;
}
</style>
